import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Spinner } from 'react-bootstrap';
import { registerUser } from '../../redux/thunk/authThunk';
import { setError, setShowLoginModal, setShowRegisterModal } from '../../redux/slices/authSlice';
import { MixGoatWordmark } from '../MixGoatLogo';
import { useDesignV2 } from '../../utils/designV2';
import SocialAuthButtons from './SocialAuthButtons';
import './RegisterModal.scss';

const RegisterModal = () => {
    const dispatch = useDispatch();
    const { isLoading, error, showRegisterModal } = useSelector((state) => state.auth);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const v2 = useDesignV2(); // v2 copy is sentence case
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const swipeStartRef = useRef(null);

    // Android hardware back + iOS swipe-from-left should go to the login
    // screen, NOT exit the app. The default `@capacitor/app` backButton
    // event on Android closes the app outright when no history exists —
    // catastrophic UX during signup. We intercept here ONLY while the
    // RegisterModal is showing.
    //
    // MUST be called unconditionally (before any early return) — a hook
    // after `if (!showRegisterModal) return null` is a conditional hook
    // and CRA's build aborts on rules-of-hooks lint. The effect's own
    // `showRegisterModal` guard handles the "do nothing" case.
    useEffect(() => {
        if (!showRegisterModal) return undefined;
        const App = window.Capacitor?.Plugins?.App;
        if (!App || typeof App.addListener !== 'function') return undefined;
        let handle = null;
        const pending = App.addListener('backButton', () => {
            dispatch(setError(null));
            setRegistrationSuccess(false);
            dispatch(setShowRegisterModal(false));
            dispatch(setShowLoginModal(true));
        });
        Promise.resolve(pending).then((h) => { handle = h; }).catch(() => {});
        return () => {
            if (handle && typeof handle.remove === 'function') {
                try { handle.remove(); } catch (_) { /* ignore */ }
            } else {
                Promise.resolve(pending).then((h) => { try { h?.remove?.(); } catch (_) {} }).catch(() => {});
            }
        };
    }, [showRegisterModal, dispatch]);

    if (!showRegisterModal) return null;

    const ERROR_FIELD_MAP = {
        'First name is required': 'firstName',
        'Last name is required': 'lastName',
        'Email is required': 'email',
        'Invalid email format': 'email',
        'Password is required': 'password',
        'Password must be at least 8 characters': 'password',
        'Passwords do not match': 'confirmPassword',
    };

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
        if (error && ERROR_FIELD_MAP[error] === field) {
            dispatch(setError(null));
        }
    };

    const validate = () => {
        if (!formData.firstName.trim()) return 'First name is required';
        if (!formData.lastName.trim()) return 'Last name is required';
        if (!formData.email.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format';
        if (!formData.password) return 'Password is required';
        if (formData.password.length < 8) return 'Password must be at least 8 characters';
        if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            dispatch(setError(validationError));
            return;
        }

        const result = await dispatch(registerUser({
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim(),
            password: formData.password,
        }));

        if (result.payload?.success) {
            setRegistrationSuccess(true);
        }
    };

    const switchToLogin = () => {
        dispatch(setError(null));
        setRegistrationSuccess(false);
        dispatch(setShowRegisterModal(false));
        dispatch(setShowLoginModal(true));
    };

    // Swipe-from-left gesture to return to login. Capacitor's `backButton`
    // event covers the Android hardware/gesture back, but iOS WKWebView has
    // no native swipe-back for SPAs — handling it in JS via touch events is
    // the only way to make "slide back" work on both. Trigger threshold: a
    // touch that starts within 28px of the left edge AND moves right by at
    // least 80px AND is mostly-horizontal.
    const handleTouchStart = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        if (t.clientX > 28) return; // only count edge starts
        swipeStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
    };
    const handleTouchMove = (e) => {
        if (!swipeStartRef.current || !e.touches || e.touches.length !== 1) return;
        const t = e.touches[0];
        const dx = t.clientX - swipeStartRef.current.x;
        const dy = Math.abs(t.clientY - swipeStartRef.current.y);
        if (dx > 80 && dx > dy * 1.5) {
            swipeStartRef.current = null;
            switchToLogin();
        }
    };
    const handleTouchEnd = () => { swipeStartRef.current = null; };

    return (
        <div
            className="auth-overlay"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div className="auth-container register-container">
                {/* Back-to-login affordance: the text link at the bottom of
                    the form is below the fold on iPhone / Android, so the
                    user couldn't see how to return to login. */}
                <button
                    type="button"
                    className="auth-back-btn"
                    onClick={switchToLogin}
                    aria-label="Back to login"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>

                <div className="auth-logo">
                    <MixGoatWordmark height={50} />
                </div>

                <h2 className="auth-title">{v2 ? 'Create your account' : 'Create Account'}</h2>
                <p className="auth-subtitle">Join MixGoat to get started</p>

                {registrationSuccess ? (
                    <div className="auth-success-message">
                        <p>Registration successful!</p>
                        <p>Please check your email to verify your account.</p>
                        <Button
                            className="auth-submit-btn"
                            onClick={switchToLogin}
                            style={{ marginTop: '20px' }}
                        >
                            Go to Login
                        </Button>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="auth-alert">
                                <span>{error}</span>
                                <button className="auth-alert-close" onClick={() => dispatch(setError(null))}>×</button>
                            </div>
                        )}

                        <Form onSubmit={handleSubmit} className="auth-form" noValidate>
                            <div className="auth-row">
                                <Form.Group className="auth-field auth-field-half">
                                    <Form.Label>{v2 ? 'First name' : 'First Name'}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="First name"
                                        value={formData.firstName}
                                        onChange={handleChange('firstName')}
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                </Form.Group>
                                <Form.Group className="auth-field auth-field-half">
                                    <Form.Label>{v2 ? 'Last name' : 'Last Name'}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Last name"
                                        value={formData.lastName}
                                        onChange={handleChange('lastName')}
                                        disabled={isLoading}
                                    />
                                </Form.Group>
                            </div>

                            <Form.Group className="auth-field">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleChange('email')}
                                    disabled={isLoading}
                                    autoComplete="email"
                                />
                            </Form.Group>

                            <Form.Group className="auth-field">
                                <Form.Label>Password</Form.Label>
                                <div className="password-input-wrapper">
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Min. 8 characters"
                                        value={formData.password}
                                        onChange={handleChange('password')}
                                        disabled={isLoading}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {showPassword ? (
                                                <>
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                                </>
                                            ) : (
                                                <>
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                    <circle cx="12" cy="12" r="3"/>
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            </Form.Group>

                            <Form.Group className="auth-field">
                                <Form.Label>{v2 ? 'Confirm password' : 'Confirm Password'}</Form.Label>
                                <div className="password-input-wrapper">
                                    <Form.Control
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm your password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange('confirmPassword')}
                                        disabled={isLoading}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        tabIndex={-1}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {showConfirmPassword ? (
                                                <>
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                                    <line x1="1" y1="1" x2="23" y2="23"/>
                                                </>
                                            ) : (
                                                <>
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                    <circle cx="12" cy="12" r="3"/>
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            </Form.Group>

                            <Button
                                type="submit"
                                className="auth-submit-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Creating account...
                                    </>
                                ) : (
                                    v2 ? 'Create account' : 'Create Account'
                                )}
                            </Button>
                        </Form>

                        {/* Every new account (email, Google, Apple — all platforms) starts
                            a card-free 14-day GOAT trial, then drops to Free with the app
                            still working. Say so where the decision is made. */}
                        <p className="auth-trial-note">
                            <strong>14 days of GOAT included.</strong> No card needed.
                        </p>

                        <SocialAuthButtons />

                        <div className="auth-footer">
                            <span className="auth-footer-text">
                                Already have an account?{' '}
                                <button
                                    className="auth-link"
                                    onClick={switchToLogin}
                                    disabled={isLoading}
                                >
                                    {v2 ? 'Log in' : 'Log In'}
                                </button>
                            </span>
                        </div>

                        <p className="auth-legal">
                            Official MixGoat account registration. By creating an account you agree to our{' '}
                            <button
                                type="button"
                                className="auth-link"
                                onClick={() => window.electron.openUrl('https://mixgoat.com/terms')}
                            >
                                Terms
                            </button>
                            {' '}and{' '}
                            <button
                                type="button"
                                className="auth-link"
                                onClick={() => window.electron.openUrl('https://mixgoat.com/privacy')}
                            >
                                Privacy Policy
                            </button>.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default RegisterModal;
